<script setup lang="ts">
import { computed, unref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBreadcrumbPagerStore } from '@/stores/breadcrumbPager'
import {
  useHeaderActionsStore,
  type HeaderAction,
  type HeaderActionValue,
} from '@/stores/headerActions'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import { useProjectsStore } from '@/stores/projects'

const route = useRoute()
const breadcrumb = useBreadcrumb()
const pager = useBreadcrumbPagerStore()
const headerActions = useHeaderActionsStore()
const projects = useProjectsStore()
const { fileSearch, reviewSave, reviewDiscard, sourceFile } = storeToRefs(headerActions)

const brandTarget = computed(() => {
  if (route.meta.customerPortal !== true) return '/projects'
  const id = Number(route.params.id)
  if (!Number.isFinite(id)) return '/projects'
  return {
    name: projects.featuresFor(id).supports_workbook ? 'customer-workbooks' : 'customer-files',
    params: { id: String(id) },
  }
})

const showHeaderActions = computed(
  () => !!fileSearch.value || !!reviewSave.value || !!reviewDiscard.value || !!sourceFile.value,
)

function actionValue<T>(value: HeaderActionValue<T> | undefined, fallback: T): T {
  return value === undefined ? fallback : unref(value)
}

function actionLabel(action: HeaderAction): string {
  return actionValue(action.label, '')
}

function actionTitle(action: HeaderAction): string | undefined {
  if (actionDisabled(action)) return undefined
  return actionValue(action.title, actionLabel(action))
}

function actionDisabled(action: HeaderAction): boolean {
  return actionValue(action.disabled, false) || actionValue(action.loading, false)
}

function actionLoading(action: HeaderAction): boolean {
  return actionValue(action.loading, false)
}

function runAction(action: HeaderAction): void {
  if (actionDisabled(action)) return
  void action.onClick()
}
</script>

<template>
  <header
    class="app-header"
    :class="{ 'app-header--resource': breadcrumb.at(-1)?.kind === 'path' }"
    data-testid="app-header"
  >
    <nav class="app-header__title" aria-label="Breadcrumb" data-testid="app-header-title">
      <RouterLink :to="brandTarget" class="app-header__brand-link" aria-label="Moria home">
        <img src="/images/moria-avatar.png" alt="" class="app-header__avatar" />
      </RouterLink>
      <span class="app-header__breadcrumbs" data-testid="app-header-breadcrumbs">
        <template v-for="(segment, idx) in breadcrumb" :key="idx">
          <span
            v-if="idx > 0"
            class="app-header__sep"
            :class="{ 'app-header__sep--path': segment.kind === 'path' }"
            aria-hidden="true"
            >{{ segment.kind === 'path' ? '›' : '/' }}</span
          >
          <RouterLink
            v-if="segment.to && idx < breadcrumb.length - 1"
            :to="segment.to"
            class="app-header__crumb app-header__crumb--link"
            :class="{
              'app-header__crumb--context': segment.kind === 'context',
              'app-header__crumb--path': segment.kind === 'path',
            }"
            :data-testid="idx === 0 ? 'app-header-crumb-root' : `app-header-crumb-${idx}`"
          >
            <span class="app-header__crumb-label">{{ segment.label }}</span>
          </RouterLink>
          <span
            v-else
            v-tooltip.bottom="{
              value: segment.kind === 'path' ? segment.label : undefined,
              showDelay: 300,
            }"
            class="app-header__crumb"
            :class="{
              'app-header__crumb--active': idx === breadcrumb.length - 1,
              'app-header__crumb--context': segment.kind === 'context',
              'app-header__crumb--path': segment.kind === 'path',
            }"
            :aria-current="idx === breadcrumb.length - 1 ? 'page' : undefined"
            :data-testid="idx === breadcrumb.length - 1 ? 'app-header-project' : undefined"
          >
            {{ segment.label }}
          </span>
        </template>
      </span>
    </nav>

    <div
      v-if="pager.active"
      class="app-header__pager"
      role="group"
      :aria-label="pager.ariaLabel"
      data-testid="app-header-pager"
    >
      <button
        v-tooltip.bottom="{ value: pager.disabledReason ?? 'Previous', showDelay: 300 }"
        type="button"
        class="app-header__pager-btn"
        :disabled="!pager.hasPrev || !!pager.disabledReason"
        aria-label="Previous"
        data-testid="app-header-pager-prev"
        @click="pager.prev()"
      >
        ‹
      </button>
      <span
        class="app-header__pager-position"
        aria-live="polite"
        data-testid="app-header-pager-position"
        >{{ pager.positionLabel }}</span
      >
      <button
        v-tooltip.bottom="{ value: pager.disabledReason ?? 'Next', showDelay: 300 }"
        type="button"
        class="app-header__pager-btn"
        :disabled="!pager.hasNext || !!pager.disabledReason"
        aria-label="Next"
        data-testid="app-header-pager-next"
        @click="pager.next()"
      >
        ›
      </button>
    </div>

    <div v-if="showHeaderActions" class="app-header__actions">
      <template v-if="sourceFile">
        <button
          v-for="action in [sourceFile.download, sourceFile.details]"
          :key="action.testId"
          type="button"
          class="app-header__action-btn"
          :disabled="actionDisabled(action)"
          :aria-label="action.iconOnly ? actionLabel(action) : undefined"
          :aria-busy="actionLoading(action)"
          :aria-expanded="
            action.expanded === undefined ? undefined : actionValue(action.expanded, false)
          "
          :aria-controls="action.controls"
          :title="actionTitle(action)"
          :data-testid="action.testId"
          @click="runAction(action)"
        >
          <i
            :class="actionLoading(action) ? 'pi pi-spinner pi-spin' : action.icon"
            aria-hidden="true"
          />
          <span v-if="!action.iconOnly">{{ actionLabel(action) }}</span>
        </button>
      </template>
      <button
        v-if="fileSearch"
        type="button"
        class="app-header__action-btn app-header__action-btn--search"
        :disabled="actionDisabled(fileSearch)"
        :title="actionTitle(fileSearch)"
        data-testid="app-header-search-files"
        @click="runAction(fileSearch)"
      >
        <i :class="fileSearch.icon" aria-hidden="true" />
        <span>{{ actionLabel(fileSearch) }}</span>
      </button>
      <button
        v-if="reviewDiscard"
        type="button"
        class="app-header__action-btn app-header__action-btn--discard"
        :disabled="actionDisabled(reviewDiscard)"
        :title="actionTitle(reviewDiscard)"
        data-testid="app-header-discard"
        @click="runAction(reviewDiscard)"
      >
        <i :class="reviewDiscard.icon" aria-hidden="true" />
        <span>{{ actionLabel(reviewDiscard) }}</span>
      </button>
      <button
        v-if="reviewSave"
        type="button"
        class="app-header__action-btn app-header__action-btn--save"
        :disabled="actionDisabled(reviewSave)"
        :aria-busy="actionLoading(reviewSave)"
        :title="actionTitle(reviewSave)"
        data-testid="app-header-save"
        @click="runAction(reviewSave)"
      >
        <i
          :class="actionLoading(reviewSave) ? 'pi pi-spinner pi-spin' : reviewSave.icon"
          aria-hidden="true"
        />
        <span>{{ actionLabel(reviewSave) }}</span>
      </button>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 0 var(--space-6);
  background: var(--bg-dark);
  border-bottom: var(--space-px) solid var(--border);
  flex-shrink: 0;
  height: var(--toolbar-height);
}

.app-header__title {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text-primary);
  min-width: 0;
  overflow: hidden;
  flex: 1 1 auto;
}

.app-header__brand-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: var(--space-12);
  min-height: var(--space-12);
  border-radius: var(--radius-md);
  text-decoration: none;
}

.app-header__breadcrumbs {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  overflow: hidden;
}
.app-header__brand-link:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-offset);
}

.app-header__avatar {
  width: var(--space-7);
  height: var(--space-7);
  border-radius: var(--radius-md);
  object-fit: cover;
  display: block;
}

.app-header__sep {
  color: var(--text-muted);
  font-weight: 400;
  flex-shrink: 0;
}

.app-header__crumb {
  color: var(--text-primary);
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}

.app-header__crumb--link:hover {
  color: var(--green-primary);
}
.app-header__crumb--link {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: min(24rem, 50vw);
  min-height: var(--space-12);
  padding: 0 var(--space-1);
  overflow: hidden;
}
.app-header__crumb-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.app-header__crumb--link:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-offset);
  border-radius: var(--radius-sm);
}

.app-header__crumb--active {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex-shrink: 1;
}

/* Context crumbs are hierarchy, not actions. Emphasize the current resource
   with type and color rather than a filled chip that reads like a button. */
.app-header__crumb--context {
  font-family: inherit;
  font-size: var(--font-size-base);
  font-weight: 600;
  padding: 0;
  background: transparent;
  color: var(--text-secondary);
}
.app-header__crumb--context.app-header__crumb--active {
  color: var(--text-primary);
}
.app-header__crumb--context.app-header__crumb--link:hover {
  color: var(--green-primary);
}

/* Path tail — resource paths appended to a workspace crumb. */
.app-header__crumb--path {
  font-family: var(--font-family-mono, ui-monospace, monospace);
  font-size: var(--font-size-sm);
  font-weight: 400;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex-shrink: 1;
}
.app-header__sep--path {
  color: var(--text-muted);
  font-weight: 400;
}

/* Pager — discrete button group rendered immediately after the last
   breadcrumb. Visually distinct from crumbs so chevrons read as a
   control, not as another segment. */
.app-header__pager {
  display: inline-flex;
  min-height: var(--space-8);
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-1);
  border: var(--space-px) solid var(--border);
  border-radius: var(--radius-md);
  background: var(--bg-elevated);
  flex-shrink: 0;
}
.app-header__pager-btn {
  display: inline-flex;
  width: var(--space-8);
  min-height: var(--space-8);
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  padding: 0;
  line-height: 1.4;
  border-radius: var(--radius-sm);
  transition:
    color var(--duration-fast),
    background var(--duration-fast);
}
.app-header__pager-btn:hover:not(:disabled) {
  color: var(--text-primary);
  background: var(--bg-card-hover);
}
.app-header__pager-btn:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--space-px);
}
.app-header__pager-btn:disabled {
  opacity: 0.3;
  cursor: default;
}
.app-header__pager-position {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
  min-width: 3.5rem;
  text-align: center;
  padding: 0 var(--space-1);
}

.app-header__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
  margin-left: auto;
}

.app-header__action-btn {
  height: 2rem;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1_5);
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
  border: var(--space-px) solid var(--border);
  background: transparent;
  color: var(--text-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition:
    color var(--duration-fast, 120ms),
    border-color var(--duration-fast, 120ms),
    background var(--duration-fast, 120ms);
}
.app-header__action-btn:hover:not(:disabled) {
  color: var(--text-primary);
  border-color: var(--border-hover, var(--border));
  background: var(--bg-card-hover);
}
.app-header__action-btn:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-offset);
}
.app-header__action-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.app-header__action-btn--save:not(:disabled) {
  border-color: var(--green-muted);
  color: var(--green-primary);
}
.app-header__action-btn--discard:hover:not(:disabled) {
  color: var(--color-danger-accent, #e25555);
  border-color: var(--color-danger-accent, #e25555);
}

@media (max-width: 64rem) {
  .app-header--resource .app-header__crumb:not(.app-header__crumb--active),
  .app-header--resource .app-header__sep,
  .app-header--resource .app-header__action-btn > span {
    display: none;
  }
}
</style>
